import { FC, useEffect, useState } from 'react';

import { Box, Card, Grid, MenuItem, Pagination, TextField } from '@mui/material';
import shallow from 'zustand/shallow';

import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import { useErrorToast } from '../../../hooks/useErrorToast';
import { EProposalStatus } from '../../../interfaces/proposal';
import { submissionTags } from '../../../interfaces/submission';
import { useProposalsStore } from '../../../stores/proposals';
import { NoResultsCard } from '../../molecules/NoResultsCard';
import { CentralisedLoading } from '../../utils/CentralisedLoading';
import { ProposalCard } from './ProposalCard';

const tags = Object.values(submissionTags).map((element) => element.value);

const PROPOSALS_LIMIT = 5;

export const ProposalList: FC = () => {
  const { total, proposals, loading, error, getProposals, currentPage, setCurrentPage } = useProposalsStore(
    ({ total, proposals, loading, error, getProposals, currentPage, setCurrentPage }) => ({ total, proposals, loading, error, currentPage, getProposals, setCurrentPage }),
    shallow,
  );
  useErrorToast(error);

  const [proposalsStatus, setProposalsStatus] = useState<string>('all');
  const [prevProposalsStatus, setPrevProposalsStatus] = useState<string>('all');
  const [proposalsCategory, setProposalsCategory] = useState<string>('all');
  const [prevProposalsCategory, setPrevProposalsCategory] = useState<string>('all');
  const [proposalsSearchInput, setProposalsSearchInput] = useState<string>('');
  const [prevProposalsSearchInput, setPrevProposalsSearchInput] = useState<string>('');

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  useEffect(() => {
    // Reset current page when changing filtered status/category/tags
    if (proposalsStatus !== prevProposalsStatus) {
      setCurrentPage(1);
      setPrevProposalsStatus(proposalsStatus);
    }
    if (proposalsCategory !== prevProposalsCategory) {
      setCurrentPage(1);
      setPrevProposalsCategory(proposalsStatus);
    }
    if (proposalsSearchInput !== prevProposalsSearchInput) {
      setCurrentPage(1);
      setPrevProposalsSearchInput(proposalsStatus);
    }
  }, [proposalsSearchInput, proposalsStatus, proposalsCategory]);

  const debouncedSearch = useDebouncedValue(proposalsSearchInput);
  useEffect(() => {
    // 'all' value is required to show 'All' value in status and tags field on the ui
    // However, this will result in Error Code 400: Field 'status' is invalid
    // As such, value needs to be clear in such a case
    const nextProposalStatus = proposalsStatus === 'all' ? '' : proposalsStatus;
    const nextProposalTag = proposalsCategory === 'all' ? '' : proposalsCategory;

    getProposals({
      search: debouncedSearch,
      'tags[]': nextProposalTag,
      status: nextProposalStatus,
      offset: (currentPage - 1) * PROPOSALS_LIMIT,
      limit: PROPOSALS_LIMIT,
    });
  }, [debouncedSearch, proposalsStatus, proposalsCategory, currentPage]);

  return (
    <>
      {/* Search and Sort Proposals */}
      <Card sx={{ boxShadow: 10, mb: 2 }}>
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
          <TextField
            size={'small'}
            fullWidth
            label={'Search'}
            value={proposalsSearchInput || ''}
            onChange={(e) => {
              setProposalsSearchInput(e.target.value || '');
            }}
            variant="outlined"
            sx={{ m: 1 }}
          />
          <TextField
            value={proposalsCategory}
            label="Category"
            select
            size="small"
            sx={{ m: 1, minWidth: 200, width: { xs: '100%', md: 'auto' } }}
            onChange={(e) => {
              setProposalsCategory(e.target.value || '');
            }}
          >
            <MenuItem value="all">All</MenuItem>
            {tags.map((tag) => (
              <MenuItem value={tag}>{tag}</MenuItem>
            ))}
          </TextField>
          <TextField
            value={proposalsStatus}
            label="Status"
            select
            size="small"
            sx={{ m: 1, minWidth: 200, width: { xs: '100%', md: 'auto' } }}
            onChange={(e) => {
              setProposalsStatus(e.target.value || '');
            }}
          >
            <MenuItem value="all">All</MenuItem>
            {Object.values(EProposalStatus).map((status) => (
              <MenuItem value={status}>{status}</MenuItem>
            ))}
          </TextField>
        </Box>
      </Card>

      {loading ? (
        <CentralisedLoading sx={{ mt: 6 }} />
      ) : (
        <Grid container spacing={2}>
          {proposals?.length ? (
            <>
              {proposals.map((proposal) => (
                <Grid xs={12} item key={proposal._id}>
                  <ProposalCard {...proposal} />
                </Grid>
              ))}
              {total && (
                <Box sx={{ mt: 3, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Pagination
                    color="primary"
                    size="large"
                    count={Math.ceil(total / PROPOSALS_LIMIT)}
                    page={currentPage}
                    onChange={handlePageChange}
                  />
                </Box>
              )}
            </>
          ) : (
            <NoResultsCard
              sx={{ width: '100%', ml: 2, mt: 3 }}
              title="No proposals match your search"
              subtitle="Please try searching with different filters!"
            />
          )}
        </Grid>
      )}
    </>
  );
};
